import { useQuery } from '@apollo/client'
import { Edit, WorkspacePremium } from '@mui/icons-material'
import {
    Box,
    Button,
    IconButton,
    Link as MuiLink,
    Stack,
    Typography,
} from '@mui/material'
import {
    DataGridPro,
    useGridApiRef,
    type GridColDef,
} from '@mui/x-data-grid-pro'
import { gql } from '__generated__'
import { GetCandidateWorkExperienceQuery } from '__generated__/graphql'
import { Fragment, useState, type FC } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ProfilePanel } from 'recruiter/components/ProfilePanel'
import { CenteredBox } from 'shared/components/CenteredBox'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import {
    formatDateWithFallback,
    formatDuration,
    getPxAsRem,
} from 'shared/utils'
import { parse, isValid } from 'date-fns'
import { CandidateExperienceDrawer } from './CandidateExperienceDrawer'

// Helper function to get sortable timestamp for work experience
const getExperienceSortTimestamp = (
    experience: CandidateWorkExperience[number]
): number => {
    if (experience.isCurrent) {
        return Number.MAX_SAFE_INTEGER
    }

    // experience end date is in MM-yyyy format
    if (experience.endDate) {
        const parsedDate = parse(experience.endDate, 'MM-yyyy', new Date())
        if (isValid(parsedDate)) {
            return parsedDate.getTime()
        }
    }

    // Invalid, null, or undefined dates go last (lowest priority)
    return Number.MIN_SAFE_INTEGER
}

export const GetCandidateWorkExperience = gql(/* GraphQL */ `
    query GetCandidateWorkExperience($candidateId: ID) {
        candidate(id: $candidateId) {
            id
            workExperience {
                id
                isCurrent
                startDate
                endDate
                company {
                    id
                    value
                }
                title {
                    id
                    value
                }
                industries {
                    id
                    topTier
                    industry {
                        id
                        value
                    }
                }
                sectors {
                    id
                    sector {
                        id
                        value
                    }
                }
                experienceDescription
                companyOffice {
                    id
                    officeLocation {
                        id
                        value
                    }
                }
                workEmail
                workPhone
            }
        }
    }
`)

const columnDefinitions: GridColDef<CandidateWorkExperience[number]>[] = [
    {
        field: 'date',
        headerName: 'Date',
        flex: 1,
        renderCell: ({ row }) => {
            const startDate = formatDateWithFallback(row.startDate, 'Unknown')
            const endDate = formatDateWithFallback(
                row.endDate,
                row.isCurrent ? 'Present' : 'Unknown'
            )
            const addColor = (date: string) => (
                <Box
                    sx={{ display: 'inline' }}
                    color={
                        date === 'Unknown'
                            ? (theme) => theme.palette.text.secondary
                            : undefined
                    }
                >
                    {date}
                </Box>
            )

            return (
                <Box>
                    {addColor(startDate)}
                    {' - '}
                    {addColor(endDate)}
                </Box>
            )
        },
    },
    {
        field: 'duration',
        headerName: 'Duration',
        flex: 1,
        renderCell: ({ row }) =>
            formatDuration(row.startDate, row.endDate, row.isCurrent) || '-',
    },
    {
        field: 'company',
        headerName: 'Company',
        flex: 1,
        renderCell: ({ row }) => (
            <MuiLink
                component={Link}
                to={`/companies/${row.company?.id}`}
                sx={(theme) => ({
                    textDecoration: 'none',
                    color: theme.palette.primary.main,
                })}
            >
                {row.company?.value}
            </MuiLink>
        ),
    },
    {
        field: 'title',
        headerName: 'Title',
        flex: 1,
        renderCell: ({ row }) => <>{row.title?.value}</>,
    },
    {
        field: 'industries',
        headerName: 'Industries',
        flex: 1,
        renderCell: ({ row: { industries } }) => {
            if (!industries || industries.length === 0) return '-'

            const industryString = industries
                .map(({ industry }) => industry.value)
                .join(', ')

            return (
                <Stack
                    height="100%"
                    justifyContent="center"
                    title={industryString}
                >
                    <Typography variant="body2" noWrap>
                        {industries.map((industry, index) => (
                            <Fragment key={industry.industry.id}>
                                {industry.topTier ? (
                                    <Box
                                        display="inline-flex"
                                        alignItems="baseline"
                                        color="primary.main"
                                        gap={getPxAsRem(4)}
                                    >
                                        <WorkspacePremium
                                            sx={{
                                                width: getPxAsRem(12),
                                                height: getPxAsRem(12),
                                            }}
                                        />
                                        {industry.industry.value}
                                    </Box>
                                ) : (
                                    industry.industry.value
                                )}

                                {index < industries.length - 1 && ', '}
                            </Fragment>
                        ))}
                    </Typography>
                </Stack>
            )
        },
    },
    {
        field: 'sectors',
        headerName: 'Sectors',
        flex: 1,
        renderCell: ({ row: { sectors } }) => {
            if (!sectors || sectors.length === 0) return '-'

            const sectorString = sectors
                .map(({ sector }) => sector.value)
                .join(', ')

            return (
                <Stack
                    height="100%"
                    justifyContent="center"
                    title={sectorString}
                >
                    <Typography variant="body2" noWrap>
                        {sectorString}
                    </Typography>
                </Stack>
            )
        },
    },
    {
        field: 'sectorDescription',
        headerName: 'Sector Description',
        flex: 1,
        renderCell: ({ row }) => <>{row.experienceDescription || '-'}</>,
    },
    {
        field: 'location',
        headerName: 'Location',
        flex: 1,
        renderCell: ({ row }) => (
            <>{row.companyOffice?.officeLocation?.value || '-'}</>
        ),
    },
]

const getNoRowsOverlay = () => (
    <CenteredBox>
        <Typography>No work experience found</Typography>
    </CenteredBox>
)

const getLoadingOverlay = () => <LoadingPanel />

interface CandidateExperiencePanelProps {}

export type CandidateWorkExperience = NonNullable<
    NonNullable<GetCandidateWorkExperienceQuery['candidate']>['workExperience']
>

export const CandidateExperiencePanel: FC<
    CandidateExperiencePanelProps
> = () => {
    const gridApi = useGridApiRef()
    const [open, setOpen] = useState(false)
    const [workExperience, setWorkExperience] =
        useState<CandidateWorkExperience>([])
    const [currentWorkExperience, setCurrentWorkExperience] = useState<
        CandidateWorkExperience[number] | null
    >(null)
    const { candidateId } = useParams()

    const { loading: isLoading } = useQuery(GetCandidateWorkExperience, {
        fetchPolicy: 'cache-and-network',
        variables: { candidateId },
        onCompleted(data) {
            const experiences = data.candidate?.workExperience ?? []

            // Sort by experience end date - most recent first
            setWorkExperience(
                [...experiences].sort((a, b) => {
                    const aTimestamp = getExperienceSortTimestamp(a)
                    const bTimestamp = getExperienceSortTimestamp(b)
                    return bTimestamp - aTimestamp
                })
            )
        },
    })

    const handleClose = () => {
        setOpen(false)
        setCurrentWorkExperience(null)
    }

    const editColumn: (typeof columnDefinitions)[number] = {
        field: 'edit',
        headerName: '',
        renderCell: ({ row }) => (
            <IconButton
                onClick={() => {
                    setOpen(true)
                    setCurrentWorkExperience(row)
                }}
            >
                <Edit />
            </IconButton>
        ),
    }

    const existingEditColumnIndex = columnDefinitions.findIndex(
        ({ field }) => field === 'edit'
    )

    if (existingEditColumnIndex !== -1) {
        columnDefinitions.splice(existingEditColumnIndex, 1, editColumn)
    } else {
        columnDefinitions.push(editColumn)
    }

    return (
        <>
            <CandidateExperienceDrawer
                open={open}
                onClose={handleClose}
                handleCancel={handleClose}
                workExperience={currentWorkExperience}
            />

            <ProfilePanel
                panelTitle="Experience"
                actionElements={[
                    <Button
                        key="add-experience"
                        variant="outlined"
                        onClick={() => setOpen(true)}
                    >
                        Add Experience
                    </Button>,
                ]}
            >
                <DataGridPro
                    sx={{
                        minHeight: workExperience.length > 0 ? 'auto' : '200px',
                    }}
                    apiRef={gridApi}
                    rows={workExperience}
                    columns={columnDefinitions}
                    loading={isLoading}
                    getRowId={(row) => row.id}
                    hideFooter
                    disableColumnFilter
                    disableColumnSelector
                    disableColumnSorting
                    disableColumnMenu
                    disableRowSelectionOnClick
                    slots={{
                        noRowsOverlay: getNoRowsOverlay,
                        loadingOverlay: getLoadingOverlay,
                    }}
                />
            </ProfilePanel>
        </>
    )
}
